import type { Action, ActionOn, ThunkOn, Computed } from 'easy-peasy';
import { action, actionOn, thunkOn, computed } from 'easy-peasy';
import type { StoreModel } from '..';
import type { ApplicationErrorOptions } from '../../types';

interface LoadingFlags {
  dynamicLimit?: boolean;
  datalake?: boolean;
  file?: boolean;
  page: number;
  workspace?: boolean;
  siteReporting?: boolean;
  fulfillmentConfig?: boolean;
  serviceStatus?: boolean;
  tracking?: boolean;
  optIns?: boolean;
  carrier?: boolean;
  ruleSet?: boolean;
  shipment?: boolean;
  label?: boolean;
  zpl?: boolean;
  labelAugmentations?: boolean;
  origin?: boolean;
  contact?: boolean;
  address?: boolean;
  schedule?: boolean;
  workflow?: boolean;
  contract?: boolean;
  carrierSettings?: boolean;
  tag?: boolean;
  packaging?: boolean;
  returnsSettings?: boolean;
  partner?: boolean;
  thirdPartyBilling?: boolean;
  fulfillmentContext?: boolean;
  upsRegistration?: boolean;
  fedexRegistration?: boolean;
  fetchCarrierAccount?: boolean;
  simulation?: boolean;
  timeInTransit?: boolean;
}

export interface LoadingModel {
  pageLoading: Computed<LoadingModel, boolean>;
  loadingFlags: LoadingFlags;
  setloadingFlag: Action<LoadingModel, LoadingFlags>;
  // Actions that will block the whole content area
  onPageLoadActionStart: ActionOn<LoadingModel, StoreModel>;
  onPageLoadActionComplete: ActionOn<LoadingModel, StoreModel>;

  // Actions that will block workspace connected components
  onWorkspaceActionStart: ActionOn<LoadingModel, StoreModel>;
  onWorkspaceActionComplete: ActionOn<LoadingModel, StoreModel>;

  // Actions that will block siteReporting connected components
  onSiteReportingActionStart: ActionOn<LoadingModel, StoreModel>;
  onSiteReportingActionComplete: ActionOn<LoadingModel, StoreModel>;

  // Actions that will block fulfillment connected components
  onFulfillmentConfigActionStart: ActionOn<LoadingModel, StoreModel>;
  onFulfillmentConfigActionComplete: ActionOn<LoadingModel, StoreModel>;

  // Actions that will block fulfillment connected components
  onFileActionStart: ActionOn<LoadingModel, StoreModel>;
  onFileActionComplete: ActionOn<LoadingModel, StoreModel>;

  // Actions that will block fulfillment connected components
  onServiceStatusActionStart: ActionOn<LoadingModel, StoreModel>;
  onServiceStatusActionComplete: ActionOn<LoadingModel, StoreModel>;

  onTrackingActionStart: ActionOn<LoadingModel, StoreModel>;
  onTrackingActionComplete: ActionOn<LoadingModel, StoreModel>;

  setLoadingTimer: ThunkOn<LoadingModel, void, StoreModel>;
  loadingFailure: Action<LoadingModel, ApplicationErrorOptions>;

  onShipmentActionStart: ActionOn<LoadingModel, StoreModel>;
  onShipmentActionComplete: ActionOn<LoadingModel, StoreModel>;

  onOptInActionStart: ActionOn<LoadingModel, StoreModel>;
  onOptInActionComplete: ActionOn<LoadingModel, StoreModel>;

  onCarrierActionStart: ActionOn<LoadingModel, StoreModel>;
  onCarrierActionComplete: ActionOn<LoadingModel, StoreModel>;

  onLabelActionStart: ActionOn<LoadingModel, StoreModel>;
  onLabelActionComplete: ActionOn<LoadingModel, StoreModel>;

  onDynamicLimitActionStart: ActionOn<LoadingModel, StoreModel>;
  onDynamicLimitActionComplete: ActionOn<LoadingModel, StoreModel>;

  onZplActionStart: ActionOn<LoadingModel, StoreModel>;
  onZplActionComplete: ActionOn<LoadingModel, StoreModel>;

  onLabelAugmentationActionStart: ActionOn<LoadingModel, StoreModel>;
  onLabelAugmentationActionComplete: ActionOn<LoadingModel, StoreModel>;

  onOriginActionStart: ActionOn<LoadingModel, StoreModel>;
  onOriginActionComplete: ActionOn<LoadingModel, StoreModel>;

  onAddressActionStart: ActionOn<LoadingModel, StoreModel>;
  onAddressActionComplete: ActionOn<LoadingModel, StoreModel>;

  onContactActionStart: ActionOn<LoadingModel, StoreModel>;
  onContactActionComplete: ActionOn<LoadingModel, StoreModel>;

  onScheduleActionStart: ActionOn<LoadingModel, StoreModel>;
  onScheduleActionComplete: ActionOn<LoadingModel, StoreModel>;

  onWorkflowActionStart: ActionOn<LoadingModel, StoreModel>;
  onWorkflowActionComplete: ActionOn<LoadingModel, StoreModel>;

  onContractActionStart: ActionOn<LoadingModel, StoreModel>;
  onContractActionComplete: ActionOn<LoadingModel, StoreModel>;

  onCarrierSettingsActionStart: ActionOn<LoadingModel, StoreModel>;
  onCarrierSettingsActionComplete: ActionOn<LoadingModel, StoreModel>;

  onTagActionStart: ActionOn<LoadingModel, StoreModel>;
  onTagActionComplete: ActionOn<LoadingModel, StoreModel>;

  onPackagingActionStart: ActionOn<LoadingModel, StoreModel>;
  onPackagingActionComplete: ActionOn<LoadingModel, StoreModel>;

  onReturnsSettingsActionStart: ActionOn<LoadingModel, StoreModel>;
  onReturnsSettingsActionComplete: ActionOn<LoadingModel, StoreModel>;

  onPartnerActionStart: ActionOn<LoadingModel, StoreModel>;
  onPartnerActionComplete: ActionOn<LoadingModel, StoreModel>;

  onFulfillmentContextActionStart: ActionOn<LoadingModel, StoreModel>;
  onFulfillmentContextActionComplete: ActionOn<LoadingModel, StoreModel>;

  onThirdPartyBillingActionStart: ActionOn<LoadingModel, StoreModel>;
  onThirdPartyBillingActionComplete: ActionOn<LoadingModel, StoreModel>;

  onUpsRegistrationActionStart: ActionOn<LoadingModel, StoreModel>;
  onUpsRegistrationActionComplete: ActionOn<LoadingModel, StoreModel>;

  onFedexRegistrationActionStart: ActionOn<LoadingModel, StoreModel>;
  onFedexRegistrationActionComplete: ActionOn<LoadingModel, StoreModel>;

  onCarrierAccountActionStart: ActionOn<LoadingModel, StoreModel>;
  onCarrierAccountActionComplete: ActionOn<LoadingModel, StoreModel>;

  onSimulationActionStart: ActionOn<LoadingModel, StoreModel>;
  onSimulationActionComplete: ActionOn<LoadingModel, StoreModel>;

  onDatalakeActionStart: ActionOn<LoadingModel, StoreModel>;
  onDatalakeActionComplete: ActionOn<LoadingModel, StoreModel>;

  onTimeInTransitActionStart: ActionOn<LoadingModel, StoreModel>;
  onTimeInTransitActionComplete: ActionOn<LoadingModel, StoreModel>;
}
let loadingTimer: NodeJS.Timeout;

export const loadingModel: LoadingModel = {
  loadingFlags: { page: 0 },
  pageLoading: computed((state) => state.loadingFlags.page > 0),
  setloadingFlag: action((state, payload) => {
    state.loadingFlags = { ...state.loadingFlags, ...payload };
  }),
  onPageLoadActionStart: actionOn(
    (actions, storeActions) => [
      storeActions.session.setRefreshing,
      storeActions.session.sessionActionStart,
      storeActions.partner.partnerActionStart,
      storeActions.packagingV2.packagingActionStart,
      storeActions.serviceStatus.serviceStatusActionStart,
      storeActions.simulation.simulationActionStart,
    ],
    (state, payload) => {
      state.loadingFlags.page += 1;
    },
  ),
  setLoadingTimer: thunkOn(
    (actions, storeActions) => [
      storeActions.session.sessionActionStart,
      storeActions.partner.partnerActionStart,
      storeActions.fulfillmentCenterConfig.fulfillmentActionStart,
      storeActions.users.userActionStart,
      storeActions.fulfillmentCenterConfig.fulfillmentActionStart,
      storeActions.simulation.simulationActionStart,
    ],
    (actions, payload, { getStoreState }) => {
      if (loadingTimer) {
        clearTimeout(loadingTimer);
      }
      loadingTimer = setTimeout(() => {
        const updatedStoreState = getStoreState();
        if (updatedStoreState.loading.pageLoading) {
          updatedStoreState.loading.loadingFlags.page = 0;
          actions.loadingFailure({
            error: new Error('Page load failure'),
            message: 'Unable to load requested page',
          });
        }
      }, 20000);
    },
  ),
  onPageLoadActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.session.authenticationSuccess,
      storeActions.session.authenticationFailure,
      storeActions.session.authenticateUser,
      storeActions.session.refreshSession,
      storeActions.session.setSessionUser,
      storeActions.session.requestPasswordReset,
      storeActions.session.fetchApplicationVersion,
      storeActions.partner.partnerConfigFailure,
      storeActions.partner.fetchPartner,
      storeActions.partner.updateVolumeEstimates,
      storeActions.partner.updateReportingConfig,
      storeActions.partner.partnerEmailVerificationFailure,
      storeActions.partner.setEmailVerified,
      storeActions.partner.setActivePartner,
      storeActions.partner.setV1AssetHistory,
      storeActions.siteReporting.fetchServiceLevelReport,
      storeActions.siteReporting.setDeliveryPerformance,
      storeActions.fulfillmentCenterConfig.fetchFulfillmentCenters,
      storeActions.serviceStatus.fetchServiceStatus,
      storeActions.security.setCreatedCredentials,
      storeActions.security.setPartnerOauthClients,
      storeActions.security.oauthClientFailure,
      storeActions.packagingV2.fetchPartnerPackagingV2,
      storeActions.packagingV2.createPartnerPackagingV2,
      storeActions.packagingV2.updatePartnerPackagingV2,
      storeActions.packagingV2.createPackagingSet,
      storeActions.packagingV2.addPackagingToSet,
      storeActions.packagingV2.fetchPackagingSets,
      storeActions.session.changePassword,
      storeActions.shipments.cancelShipment,
      storeActions.shipments.fetchShipmentById,
      storeActions.shipments.fetchLtlCostById,
      storeActions.shipments.createLabel,
      storeActions.zpl.fetchLabel,
      storeActions.zpl.fetchLabelFromUrl,
      storeActions.tags.createTag,
      storeActions.tags.fetchTagsByCriteria,
      storeActions.tags.updateTag,
      storeActions.workflow.fetchAssetsForWorkflow,
      storeActions.workflow.fetchWorkflows,
      storeActions.simulation.fetchCarrierSelectionSummaries,
      storeActions.simulation.fetchSimulationSummaries,
      storeActions.simulation.fetchSimulationDetail,
      storeActions.simulation.createSimulation,
      storeActions.simulation.updateSimulation,
      storeActions.simulation.fetchSimulationRawResults,
      storeActions.simulation.updateSimulationShipmentBatch,
      storeActions.simulation.fetchSimulationExports,
      storeActions.simulation.fetchSimulationReport,
      storeActions.simulation.setSimulationSellRateZoneMappings,
      storeActions.simulation.setCarrierSellRateZoneMappings,
      storeActions.timeInTransit.fetchTntDataSetTypes,
      storeActions.timeInTransit.fetchTntDataSets,
      storeActions.timeInTransit.fetchTntSources,
      storeActions.timeInTransit.createAsyncDataSetTypeFromUpload,
    ],
    (state, payload) => {
      state.loadingFlags.page -= 1;
    },
  ),

  onTimeInTransitActionStart: actionOn(
    (actions, storeActions) => [storeActions.timeInTransit.timeInTransitActionStart],
    (state, payload) => {
      state.loadingFlags.timeInTransit = true;
    },
  ),

  onTimeInTransitActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.timeInTransit.fetchTntDataSetTypes,
      storeActions.timeInTransit.fetchTntDataSets,
      storeActions.timeInTransit.fetchTntSources,
      storeActions.timeInTransit.fetchTntRecordsByVersion,
      storeActions.timeInTransit.createAsyncDataSetTypeFromUpload,
      storeActions.timeInTransit.createAsyncDataSetTypeFromUploadV2,
      storeActions.timeInTransit.updateDataSetTypeFromUploadV2,
    ],
    (state, payload) => {
      state.loadingFlags.timeInTransit = false;
    },
  ),

  onDatalakeActionStart: actionOn(
    (actions, storeActions) => [storeActions.datalake.datalakeActionStart],
    (state, payload) => {
      state.loadingFlags.datalake = true;
    },
  ),

  onDatalakeActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.datalake.setCarrierSelectionSummaries,
      storeActions.datalake.datalakeFailure,
    ],
    (state, payload) => {
      state.loadingFlags.datalake = false;
    },
  ),

  onSimulationActionStart: actionOn(
    (actions, storeActions) => [storeActions.simulation.simulationActionStart],
    (state, payload) => {
      state.loadingFlags.simulation = true;
    },
  ),

  onSimulationActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.simulation.fetchCarrierSelectionSummaries,
      storeActions.simulation.fetchSimulationSummaries,
      storeActions.simulation.fetchSimulationDetail,
      storeActions.simulation.createSimulation,
      storeActions.simulation.updateSimulation,
      storeActions.simulation.fetchSimulationRawResults,
      storeActions.simulation.updateSimulationShipmentBatch,
      storeActions.simulation.fetchSimulationExports,
      storeActions.simulation.fetchSimulationReport,
    ],
    (state, payload) => {
      state.loadingFlags.simulation = false;
    },
  ),

  onWorkspaceActionStart: actionOn(
    (actions, storeActions) => [storeActions.users.userActionStart],
    (state, payload) => {
      state.loadingFlags.workspace = true;
    },
  ),

  onWorkspaceActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.users.addUserFailure,
      storeActions.users.fetchUsersFailure,
      storeActions.users.setUser,
      storeActions.users.setUsers,
    ],
    (state, payload) => {
      state.loadingFlags.workspace = false;
    },
  ),
  onSiteReportingActionStart: actionOn(
    (actions, storeActions) => [storeActions.siteReporting.siteReportActionStart],
    (state, payload) => {
      state.loadingFlags.siteReporting = true;
    },
  ),

  onSiteReportingActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.siteReporting.fetchOrderToDeliverReport,
      storeActions.siteReporting.fetchServiceLevelReport,
      storeActions.siteReporting.fetchDeliveryPerformanceReport,
    ],
    (state, payload) => {
      state.loadingFlags.siteReporting = false;
    },
  ),

  onFulfillmentConfigActionStart: actionOn(
    (actions, storeActions) => [storeActions.fulfillmentCenterConfig.fulfillmentActionStart],
    (state, payload) => {
      state.loadingFlags.fulfillmentConfig = true;
    },
  ),
  onFileActionStart: actionOn(
    (actions, storeActions) => [storeActions.file.fileActionStart],
    (state, payload) => {
      state.loadingFlags.file = true;
    },
  ),
  onTrackingActionStart: actionOn(
    (actions, storeActions) => [storeActions.tracking.trackingActionStart],
    (state, payload) => {
      state.loadingFlags.tracking = true;
    },
  ),
  onTrackingActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.tracking.setTrackings,
      storeActions.tracking.trackingError,
      storeActions.tracking.setShipiumTracking,
      storeActions.tracking.setTrackingDetail,
      storeActions.tracking.getTrackingByShipiumTrackingId,
      storeActions.tracking.getTrackingByCarrierTrackingId,
    ],
    (state, payload) => {
      state.loadingFlags.tracking = false;
    },
  ),
  onFileActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.file.fetchUploads,
      storeActions.file.uploadError,
      storeActions.file.uploadSuccess,
    ],
    (state, payload) => {
      state.loadingFlags.file = false;
    },
  ),
  onFulfillmentConfigActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.fulfillmentCenterConfig.fetchFulfillmentCenters,
      storeActions.fulfillmentCenterConfig.setCarrierSchedules,
    ],
    (state, payload) => {
      state.loadingFlags.fulfillmentConfig = false;
    },
  ),

  onServiceStatusActionStart: actionOn(
    (actions, storeActions) => [storeActions.serviceStatus.serviceStatusActionStart],
    (state, payload) => {
      state.loadingFlags.serviceStatus = true;
    },
  ),
  onServiceStatusActionComplete: actionOn(
    (actions, storeActions) => [storeActions.serviceStatus.fetchServiceStatus],
    (state, payload) => {
      state.loadingFlags.serviceStatus = false;
    },
  ),

  onOptInActionStart: actionOn(
    (actions, storeActions) => [storeActions.security.securityActionStart],
    (state, payload) => {
      state.loadingFlags.optIns = true;
    },
  ),
  onOptInActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.security.setAuthorities,
      storeActions.security.oauthClientFailure,
      storeActions.security.setPartnerOauthClients,
      storeActions.security.setPartnerApiKeys,
      storeActions.security.setSelectedOauthClient,
      storeActions.security.setCreatedCredentials,
    ],
    (state, payload) => {
      state.loadingFlags.optIns = false;
    },
  ),
  onCarrierActionStart: actionOn(
    (actions, storeActions) => [storeActions.carrier.carrierActionStart],
    (state, payload) => {
      state.loadingFlags.carrier = true;
    },
  ),
  onCarrierActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.carrier.carrierFailure,
      storeActions.carrier.fetchCarrierList,
      storeActions.carrier.setCarrierList,
      storeActions.carrier.setRateCalculationTestResponse,
      storeActions.carrier.setRateCalculationTestRecords,
      storeActions.carrierAssets.setFedexHubIds,
    ],
    (state, payload) => {
      state.loadingFlags.carrier = false;
    },
  ),
  onShipmentActionStart: actionOn(
    (actions, storeActions) => [storeActions.shipments.shipmentActionStart],
    (state, payload) => {
      state.loadingFlags.shipment = true;
    },
  ),
  onShipmentActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.shipments.fetchShipmentById,
      storeActions.shipments.fetchLtlCostById,
      storeActions.shipments.shipmentActionFailure,
      storeActions.shipments.cancelShipment,
      storeActions.shipments.createLabel,
      storeActions.shipments.fetchShipmentByLabelId,
      storeActions.shipments.setDeliveryEstimate,
      storeActions.shipments.setSubscriptionTiming,
      storeActions.shipments.fetchSubscriptionTimingById,
      storeActions.shipments.fetchSubscriptionTiming,
    ],
    (state, payload) => {
      state.loadingFlags.shipment = false;
    },
  ),
  onZplActionStart: actionOn(
    (actions, storeActions) => [storeActions.zpl.zplActionStart],
    (state, payload) => {
      state.loadingFlags.zpl = true;
    },
  ),
  onZplActionComplete: actionOn(
    (actions, storeActions) => [storeActions.zpl.fetchLabel, storeActions.zpl.fetchLabelFromUrl],
    (state, payload) => {
      state.loadingFlags.zpl = false;
    },
  ),
  loadingFailure: action((state, error) => {
    console.error(error);
  }),
  onLabelActionStart: actionOn(
    (actions, storeActions) => [],
    (state, paylod) => {
      state.loadingFlags.label = true;
    },
  ),
  onLabelActionComplete: actionOn(
    (actions, storeActions) => [],
    (state, payload) => {
      state.loadingFlags.label = false;
    },
  ),
  onDynamicLimitActionStart: actionOn(
    (actions, storeActions) => [storeActions.limit.dynamicLimitActionStart],
    (state, payload) => {
      state.loadingFlags.dynamicLimit = true;
    },
  ),
  onDynamicLimitActionComplete: actionOn(
    (actions, storeActions) => [storeActions.limit.setCostAdjustConfigs],
    (state, payload) => {
      state.loadingFlags.dynamicLimit = false;
    },
  ),
  onLabelAugmentationActionStart: actionOn(
    (actions, storeActions) => [storeActions.labelAugmentations.labelAugmentationStart],
    (state, payload) => {
      state.loadingFlags.labelAugmentations = true;
    },
  ),
  onLabelAugmentationActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.labelAugmentations.setLabelAugmentations,
      storeActions.labelAugmentations.labelAugmentationFailure,
      storeActions.labelAugmentations.setLabelAugmentationSets,
    ],
    (state, payload) => {
      state.loadingFlags.labelAugmentations = false;
    },
  ),

  onOriginActionStart: actionOn(
    (actions, storeActions) => [storeActions.origins.originActionStart],
    (state, payload) => {
      state.loadingFlags.origin = true;
    },
  ),
  onOriginActionComplete: actionOn(
    (actions, storeActions) => [storeActions.origins.originFailure, storeActions.origins.setOrigins],
    (state, payload) => {
      state.loadingFlags.origin = false;
    },
  ),
  onAddressActionStart: actionOn(
    (actions, storeActions) => [storeActions.address.addressActionStart],
    (state, payload) => {
      state.loadingFlags.address = true;
    },
  ),
  onAddressActionComplete: actionOn(
    (actions, storeActions) => [storeActions.address.addressFailure, storeActions.address.setAddresses],
    (state, payload) => {
      state.loadingFlags.address = false;
    },
  ),
  onContactActionStart: actionOn(
    (actions, storeActions) => [storeActions.contact.contactActionStart],
    (state, payload) => {
      state.loadingFlags.contact = true;
    },
  ),
  onContactActionComplete: actionOn(
    (actions, storeActions) => [storeActions.contact.contactFailure, storeActions.contact.setContacts],
    (state, payload) => {
      state.loadingFlags.contact = false;
    },
  ),

  onScheduleActionStart: actionOn(
    (actions, storeActions) => [storeActions.schedule.scheduleActionStart],
    (state, payload) => {
      state.loadingFlags.schedule = true;
    },
  ),
  onScheduleActionComplete: actionOn(
    (actions, storeActions) => [storeActions.schedule.scheduleFailure, storeActions.schedule.setSchedules],
    (state, payload) => {
      state.loadingFlags.schedule = false;
    },
  ),

  onWorkflowActionStart: actionOn(
    (actions, storeActions) => [storeActions.workflow.WorkflowActionStart],
    (state, payload) => {
      state.loadingFlags.workflow = true;
    },
  ),
  onWorkflowActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.workflow.saveWorkflowFailure,
      storeActions.workflow.saveWorkflow,
      storeActions.workflow.fetchWorkflows,
      storeActions.workflow.refreshWorkflow,
      storeActions.workflow.fetchAssetsForAllWorkflows,
    ],
    (state, payload) => {
      state.loadingFlags.workflow = false;
    },
  ),
  onContractActionStart: actionOn(
    (actions, storeActions) => [storeActions.contract.contractActionStart],
    (state, payload) => {
      state.loadingFlags.contract = true;
    },
  ),
  onContractActionComplete: actionOn(
    (actions, storeActions) => [storeActions.contract.contractFailure, storeActions.contract.setContracts],
    (state, payload) => {
      state.loadingFlags.contract = false;
    },
  ),
  onCarrierSettingsActionStart: actionOn(
    (actions, storeActions) => [storeActions.carrierSettings.carrierSettingsActionStart],
    (state, payload) => {
      state.loadingFlags.contract = true;
    },
  ),
  onCarrierSettingsActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.carrierSettings.carrierSettingsFailure,
      storeActions.carrierSettings.setCarrierSettings,
    ],
    (state, payload) => {
      state.loadingFlags.contract = false;
    },
  ),
  onTagActionStart: actionOn(
    (actions, storeActions) => [storeActions.tags.tagActionStart],
    (state, payload) => {
      state.loadingFlags.tag = true;
    },
  ),
  onTagActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.tags.addTagsToCache,
      storeActions.tags.tagFailure,
      storeActions.tags.fetchTagsByCriteria,
    ],
    (state, payload) => {
      state.loadingFlags.tag = false;
    },
  ),
  onPackagingActionStart: actionOn(
    (actions, storeActions) => [storeActions.packaging.packagingActionStart],
    (state, payload) => {
      state.loadingFlags.packaging = true;
    },
  ),
  onPackagingActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.packaging.setPackagings,
      storeActions.packaging.setPackagingSets,
      storeActions.packaging.packagingFailure,
    ],
    (state, payload) => {
      state.loadingFlags.packaging = false;
    },
  ),
  onReturnsSettingsActionStart: actionOn(
    (actions, storeActions) => [storeActions.returnsSettings.returnsSettingsActionStart],
    (state, payload) => {
      state.loadingFlags.packaging = true;
    },
  ),
  onReturnsSettingsActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.returnsSettings.returnsSettingsActionStart,
      storeActions.returnsSettings.setReturnsSettings,
    ],
    (state, payload) => {
      state.loadingFlags.packaging = false;
    },
  ),
  onFulfillmentContextActionStart: actionOn(
    (actions, storeActions) => [storeActions.FP_fulfillmentContext.fulfillmentContextActionStart],
    (state, payload) => {
      state.loadingFlags.fulfillmentContext = true;
    },
  ),
  onFulfillmentContextActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.FP_fulfillmentContext.fetchFulfillmentContextDetailByContextId,
      storeActions.FP_fulfillmentContext.fetchFulfillmentContextDetails,
      storeActions.FP_fulfillmentContext.fetchAddressAssets,
      storeActions.FP_fulfillmentContext.fetchContactAssets,
      storeActions.FP_fulfillmentContext.createFulfillmentContext,
      storeActions.FP_fulfillmentContext.updateFulfillmentContext,
      storeActions.FP_fulfillmentContext.linkTenant,
      storeActions.FP_fulfillmentContext.deleteFulfillmentContext,
      storeActions.FP_fulfillmentContext.updateTenant,
      storeActions.FP_fulfillmentContext.mutateFulfillmentContextFailure,
      storeActions.FP_fulfillmentContext.updateAddress,
      storeActions.FP_fulfillmentContext.createAddress,
      storeActions.FP_fulfillmentContext.linkAddress,
      storeActions.FP_fulfillmentContext.linkTag,
      storeActions.FP_fulfillmentContext.fetchSharedAssetFailure,
      storeActions.FP_fulfillmentContext.setHydratedOrigins,
      storeActions.FP_fulfillmentContext.setOrigins,
      storeActions.FP_fulfillmentContext.linkOrigin,
      storeActions.FP_fulfillmentContext.createOrigin,
      storeActions.FP_fulfillmentContext.linkLabelAugmentation,
      storeActions.FP_fulfillmentContext.fetchLabelAugmentations,
    ],
    (state, payload) => {
      state.loadingFlags.fulfillmentContext = false;
    },
  ),
  onUpsRegistrationActionStart: actionOn(
    (actions, storeActions) => [storeActions.FP_fulfillmentContext.fetchUpsRegistrationsStart],
    (state, payload) => {
      state.loadingFlags.upsRegistration = true;
    },
  ),
  onUpsRegistrationActionComplete: actionOn(
    (actions, storeActions) => [storeActions.FP_fulfillmentContext.fetchUpsRegistrations],
    (state, payload) => {
      state.loadingFlags.upsRegistration = false;
    },
  ),
  onFedexRegistrationActionStart: actionOn(
    (actions, storeActions) => [storeActions.FP_fulfillmentContext.fetchFedexRegistrationsStart],
    (state, payload) => {
      state.loadingFlags.fedexRegistration = true;
    },
  ),
  onFedexRegistrationActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.FP_fulfillmentContext.fetchFedexRegistration,
      storeActions.FP_fulfillmentContext.fetchFedexRegistrations,
    ],
    (state, payload) => {
      state.loadingFlags.fedexRegistration = false;
    },
  ),
  onCarrierAccountActionStart: actionOn(
    (actions, storeActions) => [storeActions.FP_fulfillmentContext.fetchCarrierAccountStart],
    (state, payload) => {
      state.loadingFlags.fetchCarrierAccount = true;
    },
  ),
  onCarrierAccountActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.FP_fulfillmentContext.fetchCarrierAccount,
      storeActions.FP_fulfillmentContext.fetchCarrierAccounts,
    ],
    (state, payload) => {
      state.loadingFlags.fetchCarrierAccount = false;
    },
  ),
  onPartnerActionStart: actionOn(
    (actions, storeActions) => [storeActions.partner.partnerActionStart],
    (state, payload) => {
      state.loadingFlags.partner = true;
    },
  ),
  onPartnerActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.partner.setActivePartner,
      storeActions.partner.setV1AssetHistory,
      storeActions.partner.partnerConfigFailure,
      storeActions.partner.setEmailVerified,
    ],
    (state, payload) => {
      state.loadingFlags.partner = false;
    },
  ),
  onThirdPartyBillingActionStart: actionOn(
    (actions, storeActions) => [storeActions.thirdPartyBilling.thirdPartyBillingActionStart],
    (state, payload) => {
      state.loadingFlags.thirdPartyBilling = true;
    },
  ),
  onThirdPartyBillingActionComplete: actionOn(
    (actions, storeActions) => [
      storeActions.thirdPartyBilling.setThirdPartyBillingAccounts,
      storeActions.thirdPartyBilling.setThirdPartyBillingAccountSets,
      storeActions.thirdPartyBilling.addThirdPartyBillingAccount,
      storeActions.thirdPartyBilling.thirdPartyBillingAccountFailure,
    ],
    (state, payload) => {
      state.loadingFlags.thirdPartyBilling = false;
    },
  ),
};
